<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import Flatpickr from 'svelte-flatpickr'
    import { derived as sderived, writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'
    import { DateTime } from 'luxon'

    import { iconMap } from 'hydria/icons'
    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { session, simpleFetch, groups as ugroups } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { toDate, toLuxon } from 'hydria/utils/time'

    import { DOMAIN_EXPIRATION_IMMINENT, DOMAIN_EXPIRATION_SOON } from 'wdom/constants'
    import { Breadcrumb } from 'breadcrumb'

    import VisualDate from 'hydria/components/Date.svelte'
    import Dropdown from 'hydria/components/Dropdown.svelte'
    import PageList from 'hydria/components/PageList.svelte'
    import MiniKeyboard from 'wdom/components/MiniKeyboard.svelte'
    import TokenAnchorProgress from '../Users/User/TokenAnchorProgress.svelte'
    import TokenProgress from '../Users/User/TokenProgress.svelte'
    import ExtraFilters from './ExtraFilters.svelte'

    const LIMIT = 10
    const DELAY_MS = 1250
    type Systems = '' | 'cira' | 'opensrs' | 'webnames'
    type Order = '' | 'expiration' | 'zone_expiration' | 'score'

    const stagesOfLife = [
        { id: 'addPeriod', text: 'Add Period', short: 'Add' },
        { id: 'transferPeriod', text: 'Transfer Period', short: 'Transf' },
        { id: 'renewPeriod', text: 'Renew Period', short: 'Renew' },
        { id: 'autoRenewPeriod', text: 'Auto Renew Period', short: 'AutoR' },
        { id: 'redemptionPeriod', text: 'Redemption Period', short: 'Redempt' },
        { id: 'pendingDelete', text: 'Pending Delete', short: 'PDel' },
        { id: 'pendingRestore', text: 'Pending Restore', short: 'PRest' },
        { id: 'registered', text: 'Registered', short: 'Reg' },
    ]

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
        skip: 'int1+',
        s_by: 'string',
        s_desc: 'boolean',
        locked: '3state',
        whois_privacy: '3state',
        has_ds_records: '3state',
        has_missing_years: 'boolean',
        has_fake_expiration: 'boolean',
        system: 'string',
        expiration_lower: 'string',
        expiration_upper: 'string',
        stage_of_life: 'string',
    }
    const mappingOrder = ['q', 'skip']

    // Search options for the backend
    interface SOptions {
        q: string
        skip: number
        s_by: Order
        s_desc: boolean
        locked: null | boolean
        whois_privacy: null | boolean
        has_ds_records: null | boolean
        has_missing_years: boolean
        has_fake_expiration: boolean
        system: Systems
        expiration_lower: string
        expiration_upper: string
        stage_of_life: string
    }
    const defaults: SOptions = {
        q: '',
        skip: 0,
        s_by: '',
        s_desc: false,
        locked: null,
        whois_privacy: null,
        has_ds_records: null,
        has_missing_years: false,
        has_fake_expiration: false,
        system: '',
        expiration_lower: '',
        expiration_upper: '',
        stage_of_life: '',
    }

    // Search result from the server
    interface ISearchResult {
        items: Array<any>
        total_items: number
        domain_category: { [key: string]: string }
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    // Purpose: set initial value on Flatpickr
    let expirationUpper: Date | undefined = $state()
    let expirationLower: Date | undefined = $state()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        if (options.skip && options.skip == loadedOptions.skip) {
            // Something non-skip must have changed, so we force skip to 0
            options.skip = 0
        }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/domains' : `/domains?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/domains' : `/domains?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    let results = writable([] as any[])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0
    let searchQueryStringJson: string | undefined = $state()
    let searchQueryString = writable('')
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = {}
        if (sOptions.q != '') sdata.text = sOptions.q
        for (const [field, value] of Object.entries(sOptions)) {
            if (['q', 'skip', 's_by', 's_desc'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                    if (['has_missing_years', 'has_fake_expiration'].includes(field) && value === false) break
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (value != null && value != '') sdata[field] = value
                    break
            }
        }
        $searchQueryString = rt.simpleMapToQs(new Map(Object.entries(sOptions)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder), ['skip', 'skip', 's_by', 's_desc']).toString()

        searchQueryStringJson = encodeURIComponent(JSON.stringify(sdata))

        if (sOptions.skip > 0) sdata.skip = sOptions.skip
        sdata.limit = LIMIT
        if (sOptions.s_by != '') sdata.sort_by = sOptions.s_by
        sdata.sort_asc = !sOptions.s_desc

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/domain/1/', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = $state(0)

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []
        const now = DateTime.now()

        nItems = data.total_items

        for (const item of data.items) {
            if (item.expiration) {
                item.expiration = toLuxon(item.expiration)
                if (item.expiration.diff(now) < DOMAIN_EXPIRATION_IMMINENT) {
                    item.expirationStatus = 'imminent'
                } else if (item.expiration.diff(now) < DOMAIN_EXPIRATION_SOON) {
                    item.expirationStatus = 'soon'
                } else {
                    item.expirationStatus = 'clean'
                }
            } else {
                item.expirationStatus = 'unknown'
            }

            if (item.fake_expiration) {
                item.fake_expiration = toLuxon(item.fake_expiration)
            }

            if (item.zone_expiration != null) {
                item.zone_expiration = toLuxon(item.zone_expiration)
                if (item.zone_expiration.diff(now) < DOMAIN_EXPIRATION_IMMINENT) {
                    item.zoneExpirationStatus = 'imminent'
                } else if (item.zone_expiration.diff(now) < DOMAIN_EXPIRATION_SOON) {
                    item.zoneExpirationStatus = 'soon'
                } else {
                    item.zoneExpirationStatus = 'clean'
                }
            } else {
                item.zoneExpirationStatus = 'unknown'
            }

            let statuses: string[] = []
            if (item.status != 'ready') statuses.push($_('Not ready'))
            else {
                if (item.whois_privacy === true) statuses.push($_('privacy'))
                if (item.whois_privacy === false) statuses.push($_('no-privacy-actived'))
            }
            item.statuses = statuses.join(', ')

            litems.push(item)
        }

        results.set(litems)
        if (loading) loading = false
    }

    let text = writable(sOptions.q)

    function loadIntoForm() {
        $text = sOptions.q
        if (sOptions.expiration_lower) {
            expirationLower = toDate(sOptions.expiration_lower)
        }
        if (sOptions.expiration_upper) {
            expirationUpper = toDate(sOptions.expiration_upper)
        }
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue == sOptions.q) {
                return
            }
            textTimer = setTimeout(() => {
                sOptions.q = newValue
                textTimer = null
                setQueryString(sOptions)
            }, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })

    const systemNames: { id: 'cira' | 'opensrs' | 'webnames'; txt: string }[] = [
        { id: 'cira', txt: 'CIRA' },
        { id: 'opensrs', txt: 'OpenSRS' },
        { id: 'webnames', txt: 'WebNames' },
    ]

    const flatOpts_lower = {
        element: '#expiration_lower',
    }
    const flatOpts_upper = {
        element: '#expiration_upper',
    }

    function toggleSort(type: Order) {
        if (sOptions.s_by == type) sOptions.s_desc = !sOptions.s_desc
        else {
            sOptions = { ...sOptions, s_by: type, s_desc: type == 'score' }
        }
        setQueryString(sOptions)
    }

    const crumbs = sderived(searchQueryString, ($searchQueryString) => [
        { txt: $_('Domains'), url: spath('/domains') },
        { txt: iconMap, html: true, urlClasses: 'btn btn-outline-secondary btn-sm', url: spath('/domains/map' + ($searchQueryString ? `?${$searchQueryString}` : '')), extra: true },
    ])

    let showFilters = $state(false)
</script>

<style>
    .form-domain-list {
        display: grid;
        gap: 0.25em;
        grid-template-columns: minmax(537px, 3fr) minmax(136px, 1fr);
        grid-template-rows: 1fr 1fr;
        grid-template-areas: 'text expmin' 'toggles expmax';
    }
    .with-map {
        grid-template-rows: 1fr 1fr 1fr;
        grid-template-areas: 'text expmin' 'toggles expmax' 'group .';
    }
    @media (min-width: 1490px) {
        .form-domain-list {
            grid-template-columns: minmax(200px, 1fr) 160px 160px auto;
            grid-template-rows: 1fr;
            grid-template-areas: 'text expmin expmax toggles';
        }

        .form-domain-list.with-map {
            grid-template-columns: minmax(200px, 1fr) 160px 160px auto;
            grid-template-rows: 1fr 1fr;
            grid-template-areas: 'text expmin expmax toggles' '. . . group';
        }
    }
    .fdl-text {
        grid-area: text;
    }
    .fdl-expiration-min {
        grid-area: expmin;
    }
    .fdl-expiration-max {
        grid-area: expmax;
    }
    .fdl-toggles {
        /* white-space: nowrap; */
        grid-area: toggles;
        justify-self: end;
    }

    .fdl-grouping {
        grid-area: group;
        justify-self: end;
    }

    .sort-head {
        display: flex;
        gap: 0.5rem;
    }
    .sort-head > :first-child {
        flex: 1 1 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    th.idna,
    td.idna {
        /* max-width: 25rem; */
        word-break: break-all;
        word-wrap: anywhere;
    }
    th.https-link,
    td.https-link {
        width: 2.35em;
        text-align: center;
        padding-right: 0.1rem;
    }
    th.http-link,
    td.http-link {
        width: 2.85em;
        font-size: 0.65rem;
        vertical-align: baseline;
        text-align: center;
        padding-left: 0.1rem;
    }

    th.lock,
    td.lock,
    th.privacy,
    td.privacy {
        width: 2.25rem;
        text-align: center;
    }

    th.tags,
    td.tags {
        max-width: 12rem;
        width: 12rem;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    td.tags .enum span {
        white-space: nowrap;
    }
    td.tags.no-tags {
        color: #aaa;
    }

    th.ucat,
    td.ucat {
        max-width: 8rem;
        width: 8rem;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    td.ucat span {
        white-space: nowrap;
    }
    td.ucat.no-cat {
        color: #aaa;
    }

    th.sol {
        border-bottom: 1px solid #aaa;
        text-align: center;
    }
    td.sol-n {
        width: 9rem;
        text-align: right;
    }
    td.sol-e {
        width: 8rem;
        font-size: 0.8em;
        vertical-align: middle;
    }

    th.domain-expiration,
    td.domain-expiration {
        width: 11rem;
    }
    th.zone-expiration,
    td.zone-expiration {
        width: 10rem;
    }

    th.score,
    td.score {
        width: 4rem;
        text-align: right;
    }
</style>

<Breadcrumb {crumbs} />

<div class="card mb-4">
    <div class="card-title text-primary">{$_('Search')}</div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
                submit()
            }}
        >
            <div class="form-domain-list">
                <div class="fdl-text">
                    <div class="input-group">
                        <input type="text" class="form-control" id="domain-text" placeholder={$_('Type a domain or a name server')} bind:this={mainInput} bind:value={$text} />
                        {#if $text}
                            <button
                                class="btn btn-secondary"
                                type="button"
                                disabled={updating}
                                onclick={() => {
                                    $text = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                        <button type="button" class="btn btn-outline-secondary" onclick={() => (showFilters = true)}><Icon inline icon="fa:filter" /></button>
                        <MiniKeyboard eid={'domain-text'} />
                        <button class="btn btn-outline-primary search" type="submit"><Icon inline icon="fa:search" /></button>
                    </div>
                </div>

                <div class="fdl-expiration-min">
                    <Flatpickr
                        options={flatOpts_lower}
                        value={expirationLower}
                        formattedValue={sOptions.expiration_lower}
                        on:valueUpdate={(e) => {
                            sOptions.expiration_lower = e.detail[1]
                            setQueryString(sOptions)
                        }}
                        dateFormat="Y-m-d"
                        element="#expiration_lower"
                    >
                        <div id="expiration_lower" class="input-group">
                            <input type="text" class="form-control" data-input placeholder="Expiration min" />
                            <button
                                class="btn btn-outline-secondary"
                                type="button"
                                onclick={() => {
                                    sOptions.expiration_lower = ''
                                    setQueryString(sOptions)
                                }}
                                data-clear
                                disabled={!sOptions.expiration_lower}><Icon inline icon="fa:times" /></button
                            >
                        </div>
                    </Flatpickr>
                </div>
                <div class="fdl-expiration-max">
                    <Flatpickr
                        options={flatOpts_upper}
                        value={expirationUpper}
                        formattedValue={sOptions.expiration_upper}
                        on:valueUpdate={(e) => {
                            sOptions.expiration_upper = e.detail[1]
                            setQueryString(sOptions)
                        }}
                        dateFormat="Y-m-d"
                        element="#expiration_upper"
                    >
                        <div id="expiration_upper" class="input-group">
                            <input type="text" class="form-control" data-input placeholder="Expiration max" />
                            <button
                                class="btn btn-outline-secondary"
                                type="button"
                                onclick={() => {
                                    sOptions.expiration_upper = ''
                                    setQueryString(sOptions)
                                }}
                                data-clear
                                disabled={!sOptions.expiration_upper}><Icon inline icon="fa:times" /></button
                            >
                        </div>
                    </Flatpickr>
                </div>

                <div class="fdl-toggles">
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.locked === true}
                            onclick={() => {
                                sOptions.locked = sOptions.locked === true ? null : true
                                setQueryString(sOptions)
                            }}
                            data-tooltip={'« locked » (transfer)'}><Icon inline icon="fa:lock" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.locked === false}
                            onclick={() => {
                                sOptions.locked = sOptions.locked === false ? null : false
                                setQueryString(sOptions)
                            }}
                            data-tooltip={'non « locked » (transfer)'}><Icon inline icon="fa:unlock" /></button
                        >
                    </div>
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.whois_privacy === true}
                            onclick={() => {
                                sOptions.whois_privacy = sOptions.whois_privacy === true ? null : true
                                setQueryString(sOptions)
                            }}
                            data-tooltip={'avec « Whois Privacy »'}><Icon inline icon="fa:eye-slash" /></button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.whois_privacy === false}
                            onclick={() => {
                                sOptions.whois_privacy = sOptions.whois_privacy === false ? null : false
                                setQueryString(sOptions)
                            }}
                            data-tooltip={'sans « Whois Privacy »'}><Icon inline icon="fa:eye" /></button
                        >
                    </div>
                    <div class="btn-group">
                        {#each systemNames as sys}
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.system === sys.id}
                                onclick={() => {
                                    sOptions.system = sOptions.system === sys.id ? '' : sys.id
                                    setQueryString(sOptions)
                                }}>{sys.txt}</button
                            >
                        {/each}
                    </div>
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.has_ds_records === true}
                            onclick={() => {
                                sOptions.has_ds_records = sOptions.has_ds_records === true ? null : true
                                setQueryString(sOptions)
                            }}
                            data-tooltip={'DNSSEC activé'}>DS</button
                        >
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.has_ds_records === false}
                            onclick={() => {
                                sOptions.has_ds_records = sOptions.has_ds_records === false ? null : false
                                setQueryString(sOptions)
                            }}
                            data-tooltip={'DNSSEC désactivé'}
                            style="text-decoration: line-through;">DS</button
                        >
                    </div>
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.has_missing_years === true}
                            onclick={() => {
                                sOptions.has_missing_years = !sOptions.has_missing_years
                                setQueryString(sOptions)
                            }}
                            data-tooltip="Années non-activées"><Icon inline icon="mdi:calendar-plus-outline" /></button
                        >
                    </div>
                    <div class="btn-group">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={updating}
                            class:active={sOptions.has_fake_expiration === true}
                            onclick={() => {
                                sOptions.has_fake_expiration = !sOptions.has_fake_expiration
                                setQueryString(sOptions)
                            }}
                            data-tooltip="Expiration temporaire"><Icon inline icon="mdi:calendar-end-outline" /></button
                        >
                    </div>

                    <div class="btn-group">
                        <Dropdown
                            items={stagesOfLife}
                            placeholder="SOL"
                            onSelect={(e) => {
                                sOptions.stage_of_life = e
                                setQueryString(sOptions)
                            }}
                            text={'text'}
                            selectedText={'short'}
                            id={'id'}
                            selected={sOptions.stage_of_life}
                            empty={''}
                            clear={'Tout'}
                            disabled={updating}
                            alignRight={true}
                            block={true}
                        />
                    </div>
                </div>
            </div>
        </form>
    </div>
</div>

{#if loading}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card">
        <div class="card-title text-primary">{$_('Domains')}</div>

        {#if $results.length}
            <div class="card-body">
                <table id="domain-list" class="table table-striped mb-0" class:disabled-look={updating}>
                    <thead>
                        <tr>
                            <th
                                class="idna"
                                onclick={() => {
                                    toggleSort('')
                                }}
                            >
                                <span class="sort-head">
                                    <span>{$_('Name')}</span>
                                    {#if sOptions.s_by == ''}
                                        <span><Icon inline icon={sOptions.s_desc ? 'mdi:chevron-down' : 'mdi:chevron-up'} /></span>
                                    {/if}
                                </span>
                            </th>
                            <th class="https-link"></th>
                            <th class="http-link"></th>
                            <th class="lock"><Icon inline icon="fa:lock" /></th>
                            <th class="privacy"><Icon inline icon="fa:eye-slash" /></th>
                            <th class="tags">Tags</th>
                            <th class="ucat">Catégorie</th>
                            <th class="sol" colspan="2">SoL</th>
                            <th
                                class="domain-expiration"
                                onclick={() => {
                                    toggleSort('expiration')
                                }}
                            >
                                <span class="sort-head">
                                    <span>{$_('Expiration')}</span>
                                    {#if sOptions.s_by == 'expiration'}
                                        <span><Icon inline icon={sOptions.s_desc ? 'mdi:chevron-down' : 'mdi:chevron-up'} /></span>
                                    {/if}
                                </span>
                            </th>
                            <th
                                class="zone-expiration"
                                onclick={() => {
                                    toggleSort('zone_expiration')
                                }}
                            >
                                <span class="sort-head">
                                    <span>{$_('Zone expiration')}</span>
                                    {#if sOptions.s_by == 'zone_expiration'}
                                        <span><Icon inline icon={sOptions.s_desc ? 'mdi:chevron-down' : 'mdi:chevron-up'} /></span>
                                    {/if}
                                </span>
                            </th>
                            <th
                                class="score"
                                onclick={() => {
                                    toggleSort('score')
                                }}
                            >
                                <span class="sort-head">
                                    <span><Icon inline icon="mdi:sort-variant" /></span>
                                    {#if sOptions.s_by == 'score'}
                                        <span><Icon inline icon={sOptions.s_desc ? 'mdi:chevron-down' : 'mdi:chevron-up'} /></span>
                                    {/if}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $results as item (item.idna)}
                            <tr class="expiration-{item.expirationStatus} zone-exp-{item.zoneExpirationStatus}">
                                <td class="idna"><a use:sanchor href={spath(`/domain/${item.idna}`)}>{item.domain}.{item.tld}</a></td>

                                <td class="https-link">
                                    <a href="https://{item.idna}" target="_blank"><Icon inline icon="fa:external-link" /></a>
                                </td>
                                <td class="http-link">
                                    <a href="http://{item.idna}" target="_blank">http<s>s</s></a>
                                </td>

                                <td class="lock">
                                    <Icon inline icon={item.locked ? 'fa:dot-circle-o' : 'fa:circle-o'} />
                                </td>
                                <td class="privacy">
                                    {#if item.whois_privacy != null}
                                        <Icon inline icon={item.whois_privacy ? 'fa:eye' : 'fa:vide'} />
                                    {:else}
                                        <span>–</span>
                                    {/if}
                                </td>

                                <td class="tags" class:has-tags={item.tags && item.tags.length} class:no-tags={!item.tags || !item.tags.length}>
                                    {#if item.tags && item.tags.length}
                                        {#each item.tags as tag}
                                            <span class="enum"><span>{tag}</span></span>
                                        {/each}
                                    {:else}
                                        –
                                    {/if}
                                </td>

                                <td class="ucat" class:has-cat={item.user_category} class:no-cat={!item.user_category}>
                                    {#if item.user_category}
                                        <span>{item.user_category}</span>
                                    {:else}
                                        <span>–</span>
                                    {/if}
                                </td>

                                <!-- <td class="sol">
                                    {item.stage_of_life} / <VisualDate value={item.stage_of_life_end} format="h-table" />
                                </td> -->
                                <td class="sol-n">
                                    {item.stage_of_life}
                                </td>
                                <td class="sol-e">
                                    <VisualDate value={item.stage_of_life_end} format="h-table" />
                                </td>
                                <td class="domain-expiration">
                                    <VisualDate value={item.expiration} format="h-table" />
                                    {#if item.fake_expiration && item.expiration && item.fake_expiration > item.expiration}
                                        <span class="text-danger tt-left tt-below d-block" data-tooltip="Expiration temporaire">
                                            <VisualDate value={item.fake_expiration} format="h-table" />
                                            <Icon inline icon="mdi:calendar-start" />
                                        </span>
                                    {/if}
                                </td>
                                <td class="zone-expiration">
                                    {#if item.zone_permanent}∞{:else}<VisualDate value={item.zone_expiration} format="h-table" />{/if}
                                </td>

                                <td class="score">{item._score}</td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
        <div class="card-footer">
            <PageList
                loading={updating}
                {nItems}
                limit={LIMIT}
                skip={sOptions.skip}
                setSkip={(e) => {
                    sOptions.skip = e
                    setQueryString(sOptions)
                }}
            />
        </div>
    </div>

    {#if $ugroups.has('wdom-user-admin') && $ugroups.has('global-ecom-admin')}
        <div class="card mt-4">
            <div class="card-title text-primary">{$_('Rapports TSV')}</div>
            <div class="card-body d-flex gap-2 flex-wrap">
                <div class="btn-group">
                    <button type="button" disabled class="btn btn-outline-secondary">Domaines :</button>
                    <TokenAnchorProgress noDisplay={true} class="btn btn-outline-secondary" href="/json/domain/1/all-domains/tsv?data={searchQueryStringJson}">
                        Tous{#if searchQueryStringJson != '%7B%7D'}<sup>*</sup>{/if}
                    </TokenAnchorProgress>
                    <TokenAnchorProgress noDisplay={true} class="btn btn-outline-secondary" href="/json/domain/1/all-domains/tsv?deleted=1">Détruits</TokenAnchorProgress>
                </div>
                <div class="btn-group">
                    <button type="button" disabled class="btn btn-outline-secondary">Domaines/contacts :</button>
                    <TokenAnchorProgress noDisplay={true} class="btn btn-outline-secondary" href="/json/domain/1/all-domain-with-contacts/tsv?data={searchQueryStringJson}">
                        Tous{#if searchQueryStringJson != '%7B%7D'}<sup>*</sup>{/if}
                    </TokenAnchorProgress>
                    <TokenAnchorProgress noDisplay={true} class="btn btn-outline-secondary" href="/json/domain/1/all-domain-with-contacts/tsv?deleted=1">Détruits</TokenAnchorProgress>
                    <TokenAnchorProgress noDisplay={true} class="btn btn-outline-secondary" href="/json/domain/1/cira-domain-with-contacts/tsv?data={searchQueryStringJson}&deleted=1">
                        CIRA + IDs{#if searchQueryStringJson != '%7B%7D'}<sup>*</sup>{/if}
                    </TokenAnchorProgress>
                </div>
                <div class="btn-group">
                    <TokenAnchorProgress noDisplay={true} class="btn btn-outline-secondary" href="/json/domain/1/contacts-with-unk-postal-or-non-ca/tsv">Contact: hors Canada ou codes postaux inconnus</TokenAnchorProgress>
                </div>
                <div class="btn-group">
                    {#if $results.length && searchQueryStringJson != null}
                        <TokenAnchorProgress noDisplay={true} class="btn btn-outline-secondary" href="/json/domain/1/ns-clients/tsv?data={searchQueryStringJson}">Clients avec NS<sup>**</sup></TokenAnchorProgress>
                    {:else}
                        <button type="button" class="btn btn-outline-secondary" disabled>Clients avec NS<sup>**</sup></button>
                    {/if}
                </div>
                <div class="btn-group">
                    {#if $results.length && searchQueryStringJson != null}
                        <TokenAnchorProgress noDisplay={true} class="btn btn-outline-secondary" href="/json/domain/1/domain-zone/tsv?data={searchQueryStringJson}">Clients/dom/zone<sup>*</sup></TokenAnchorProgress>
                    {:else}
                        <button type="button" class="btn btn-outline-secondary" disabled>Clients/dom/zone<sup>*</sup></button>
                    {/if}
                </div>
                <div class="btn-group">
                    {#if $results.length && searchQueryStringJson != null}
                        <TokenAnchorProgress noDisplay={true} class="btn btn-outline-secondary" href="/json/domain/1/domain-tags/tsv?data={searchQueryStringJson}">Client/dom/tags<sup>*</sup></TokenAnchorProgress>
                    {:else}
                        <button type="button" class="btn btn-outline-secondary" disabled>Client/dom/tag<sup>*</sup></button>
                    {/if}
                </div>
                <div class="small" style="flex: 1 0 100%">
                    <sup>*</sup> : Basé sur la recherche de la liste ci-haut. &nbsp;&nbsp;&nbsp; Expiration des liens : <TokenProgress />
                </div>
            </div>
        </div>
    {/if}
{/if}

{#if showFilters}
    <ExtraFilters
        close={() => (showFilters = false)}
        filter={(e) => {
            showFilters = false
            $text = e
            submit()
        }}
    />
{/if}
